/**
 * PaymentManagement Component
 * 
 * Main payment management interface for admins
 * Includes payment list, details, and refund capabilities
 */
import React, { useState, useEffect } from 'react';
import { 
  Search,
  Filter,
  Download,
  RefreshCw,
  Eye,
  DollarSign,
  AlertCircle
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../common';
import { H3, Text } from '../../../common/Typography';
import { Button } from '../../../common';
import PaymentsList from './PaymentsList';
import PaymentDetails from './PaymentDetails';
import RefundDialog from './RefundDialog';
import { paymentService } from '../../../services/api/paymentService';

const PaymentManagement = () => {
  const [activeTab, setActiveTab] = useState('list');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch all payments for admin dashboard
      const response = await paymentService.getAllPayments();
      // paymentService now returns array directly, not wrapped in payments property
      setPayments(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError('Failed to load payments');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSelect = (payment) => {
    setSelectedPayment(payment);
    setActiveTab('details');
  };

  const handleRefundSuccess = (updatedPayment) => {
    setPayments(payments.map(p => p.id === updatedPayment.id ? updatedPayment : p));
    setSelectedPayment(updatedPayment);
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = !searchTerm || 
      payment.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.applicationId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const tabs = [
    { id: 'list', label: 'All Payments', icon: DollarSign },
    { id: 'details', label: 'Payment Details', icon: Eye, disabled: !selectedPayment },
    { id: 'refunds', label: 'Refund Manager', icon: RefreshCw }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <H3 className="mb-2">Payment Management</H3>
          <Text className="text-gray-600">
            Manage payments, process refunds, and view transaction details
          </Text>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={fetchPayments}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const isDisabled = tab.disabled;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => !isDisabled && setActiveTab(tab.id)}
                    disabled={isDisabled}
                    className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      isActive
                        ? 'border-[#00796b] text-[#00796b]'
                        : isDisabled
                        ? 'border-transparent text-gray-400 cursor-not-allowed'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Search and Filters for List tab */}
            {activeTab === 'list' && (
              <div className="mb-6 flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by email, application ID, or payment ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="succeeded">Succeeded</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                    <option value="partially_refunded">Partially Refunded</option>
                  </select>
                </div>
              </div>
            )}

            {/* Tab Content */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-4 w-4" />
                  <Text>{error}</Text>
                </div>
              </div>
            )}

            {activeTab === 'list' && (
              <PaymentsList
                payments={filteredPayments}
                isLoading={isLoading}
                onPaymentSelect={handlePaymentSelect}
              />
            )}

            {activeTab === 'details' && selectedPayment && (
              <PaymentDetails
                payment={selectedPayment}
                onBack={() => setActiveTab('list')}
              />
            )}

            {activeTab === 'refunds' && (
              <RefundDialog
                payments={payments.filter(p => p.status === 'succeeded' || p.status === 'partially_refunded')}
                onRefundSuccess={handleRefundSuccess}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentManagement;
